import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { 
  ProfilePermissionOverride, 
  ProfilePermissionOverrideApiResponse, 
  ProfilePermissionOverrideDto,
  ProfilePermissionOverrideCreate,
  ProfilePermissionOverrideUpdate
} from '../models/ProfilePermissionOverride';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProfilePermissionOverrideService {
  // Usa proxy em desenvolvimento e host do microserviço em produção
   private baseUrl = environment.microservices.profileGroup;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // ===== CRUD de ProfilePermissionOverride =====

  /**
   * Busca todos os overrides com paginação
   */
  getOverrides(page: number = 1, pageSize: number = 10): Observable<ProfilePermissionOverrideApiResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfilePermissionOverrideApiResponse>(`${this.baseUrl}/GetProfilePermissionOverrides`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar overrides de permissões');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os overrides e retorna apenas os dados mapeados
   */
  getAllOverrides(): Observable<ProfilePermissionOverride[]> {
    return this.getOverrides().pipe(
      map(response => response.data.map(dto => this.mapFromOverrideDTO(dto)))
    );
  }

  /**
   * Busca override por ID
   */
  getOverrideById(id: number): Observable<ProfilePermissionOverride> {
    return this.http.get<ProfilePermissionOverrideDto>(`${this.baseUrl}/GetProfilePermissionOverrides/${id}`).pipe(
      map(dto => this.mapFromOverrideDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar override');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria um novo override
   */
  createOverride(overrideData: ProfilePermissionOverrideCreate): Observable<ProfilePermissionOverride> {
    return this.http.post<ProfilePermissionOverrideDto>(`${this.baseUrl}/CreateProfilePermissionOverride`, overrideData).pipe(
      map(dto => this.mapFromOverrideDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar override de permissão');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um override existente
   */
  updateOverride(id: number, overrideData: ProfilePermissionOverrideUpdate): Observable<ProfilePermissionOverride> {
    return this.http.put<ProfilePermissionOverrideDto>(`${this.baseUrl}/UpdateProfilePermissionOverride/${id}`, overrideData).pipe(
      map(dto => this.mapFromOverrideDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar override');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove um override (soft delete)
   */
  deleteOverride(id: number, deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeleteProfilePermissionOverride/${id}`, {
      body: { deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao excluir override');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Busca e Filtro =====

  /**
   * Busca overrides por perfil
   */
  getOverridesByProfile(profileId: number, page: number = 1, pageSize: number = 10): Observable<ProfilePermissionOverrideApiResponse> {
    const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfilePermissionOverrideApiResponse>(`${this.baseUrl}/GetProfilePermissionOverrides`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar overrides por perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os overrides de um perfil e retorna apenas os dados mapeados
   */
  getAllOverridesByProfile(profileId: number): Observable<ProfilePermissionOverride[]> {
    return this.getOverridesByProfile(profileId, 1, 1000).pipe(
      map(response => response.data.map(dto => this.mapFromOverrideDTO(dto)))
    );
  }

  /**
   * Busca overrides por permissão
   */
  getOverridesByPermission(permissionId: number, page: number = 1, pageSize: number = 10): Observable<ProfilePermissionOverrideApiResponse> {
    const params = new HttpParams()
      .set('permissionId', permissionId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfilePermissionOverrideApiResponse>(`${this.baseUrl}/GetProfilePermissionOverrides`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar overrides por permissão');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os overrides de uma permissão e retorna apenas os dados mapeados
   */
  getAllOverridesByPermission(permissionId: number): Observable<ProfilePermissionOverride[]> {
    return this.getOverridesByPermission(permissionId).pipe(
      map(response => response.data.map(dto => this.mapFromOverrideDTO(dto)))
    );
  }

  /**
   * Busca overrides por tipo
   */
  getOverridesByType(overrideType: 'allow' | 'deny' | 'custom', page: number = 1, pageSize: number = 10): Observable<ProfilePermissionOverrideApiResponse> {
    const params = new HttpParams()
      .set('overrideType', overrideType)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfilePermissionOverrideApiResponse>(`${this.baseUrl}/GetProfilePermissionOverrides`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar overrides por tipo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca overrides ativos
   */
  getActiveOverrides(page: number = 1, pageSize: number = 10): Observable<ProfilePermissionOverrideApiResponse> {
    const params = new HttpParams()
      .set('isActive', 'true')
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfilePermissionOverrideApiResponse>(`${this.baseUrl}/GetProfilePermissionOverrides`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar overrides ativos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os overrides ativos e retorna apenas os dados mapeados
   */
  getAllActiveOverrides(): Observable<ProfilePermissionOverride[]> {
    return this.getActiveOverrides().pipe(
      map(response => response.data.map(dto => this.mapFromOverrideDTO(dto)))
    );
  }

  /**
   * Busca overrides por perfil e permissão específicos
   */
  getOverrideByProfileAndPermission(profileId: number, permissionId: number): Observable<ProfilePermissionOverride | null> {
    const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('permissionId', permissionId.toString());

    return this.http.get<ProfilePermissionOverrideApiResponse>(`${this.baseUrl}/GetProfilePermissionOverrides`, { params }).pipe(
      map(response => {
        if (response.data && response.data.length > 0) {
          return this.mapFromOverrideDTO(response.data[0]);
        }
        return null;
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar override específico');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Validação =====

  /**
   * Verifica se já existe um override para o perfil e permissão
   */
  checkOverrideExists(profileId: number, permissionId: number, excludeId?: number): Observable<{ exists: boolean }> {
    let params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('permissionId', permissionId.toString());
    
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    
    return this.http.get<{ exists: boolean }>(
      `${this.baseUrl}/CheckOverrideExists`, 
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao verificar existência de override');
        return throwError(() => err);
      })
    );
  }

  /**
   * Verifica se o override pode ser deletado
   */
  canDeleteOverride(id: number): Observable<{ canDelete: boolean; reason?: string }> {
    return this.http.get<{ canDelete: boolean; reason?: string }>(
      `${this.baseUrl}/CanDeleteOverride/${id}`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao verificar se o override pode ser excluído');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Auditoria =====

  /**
   * Busca histórico de alterações de um override
   */
  getOverrideHistory(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/GetOverrideHistory/${id}`).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar histórico do override');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca overrides criados por um usuário
   */
  getOverridesByCreator(createdBy: string, page: number = 1, pageSize: number = 10): Observable<ProfilePermissionOverrideApiResponse> {
    const params = new HttpParams()
      .set('createdBy', createdBy)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfilePermissionOverrideApiResponse>(`${this.baseUrl}/GetOverridesByCreator`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar overrides por criador');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Operações em Lote =====

  /**
   * Cria múltiplos overrides de uma vez
   */
  createMultipleOverrides(overrides: ProfilePermissionOverrideCreate[]): Observable<ProfilePermissionOverride[]> {
    return this.http.post<ProfilePermissionOverrideDto[]>(`${this.baseUrl}/CreateMultipleOverrides`, overrides).pipe(
      map(dtos => dtos.map(dto => this.mapFromOverrideDTO(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar múltiplos overrides');
        return throwError(() => err);
      })
    );
  }

  /**
   * Deleta múltiplos overrides de uma vez
   */
  deleteMultipleOverrides(ids: number[], deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeleteMultipleOverrides`, {
      body: { ids, deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao excluir múltiplos overrides');
        return throwError(() => err);
      })
    );
  }

  /**
   * Ativa ou desativa um override
   */
  toggleOverrideStatus(id: number, isActive: boolean, updatedBy: string): Observable<ProfilePermissionOverride> {
    return this.updateOverride(id, { isActive, updatedBy });
  }

  // ===== Mapeamento de Dados =====

  /**
   * Mapeia dados recebidos da API para o modelo ProfilePermissionOverride
   */
  private mapFromOverrideDTO(dto: ProfilePermissionOverrideDto): ProfilePermissionOverride {
    return {
      id: dto.Id,
      profileId: dto.ProfileId,
      permissionId: dto.PermissionId,
      overrideType: dto.OverrideType,
      customName: dto.CustomName,
      customDescription: dto.CustomDescription,
      isActive: dto.IsActive,
      createdAt: new Date(dto.CreatedAt),
      updatedAt: new Date(dto.UpdatedAt),
      createdBy: dto.CreatedBy,
      updatedBy: dto.UpdatedBy,
      isDeleted: dto.IsDeleted,
      deletedAt: dto.DeletedAt ? new Date(dto.DeletedAt) : undefined,
      deletedBy: dto.DeletedBy
    };
  }

  /**
   * Mapeia dados do modelo ProfilePermissionOverride para envio à API
   */
  private mapToOverrideDTO(payload: any): any {
    return {
      profileId: payload.profileId,
      permissionId: payload.permissionId,
      overrideType: payload.overrideType,
      customName: payload.customName || '',
      customDescription: payload.customDescription || '',
      isActive: payload.isActive !== undefined ? payload.isActive : true,
      createdBy: payload.createdBy,
      updatedBy: payload.updatedBy
    };
  }
}
